import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import axios from 'axios'
import { apiClient } from '../api/client'
import { resolveTenantSlug } from '../lib/tenant'

type HttpMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export type OutboxItem = {
  id: string
  tenantSlug: string | null
  method: HttpMethod
  url: string
  body: unknown
  createdAt: number
  attempts: number
  lastAttemptAt: number | null
  lastError: string | null
}

type OutboxState = {
  items: OutboxItem[]
  enqueue: (item: Omit<OutboxItem, 'id' | 'createdAt' | 'attempts' | 'lastAttemptAt' | 'lastError'>) => string
  remove: (id: string) => void
  flush: () => Promise<void>
  pendingCount: () => number
  clearTenant: (tenantSlug: string) => void
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `obx_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

export const useOutboxStore = create<OutboxState>()(
  persist(
    (set, get) => ({
      items: [],

      enqueue: (item) => {
        const id = randomId()
        const record: OutboxItem = {
          id,
          tenantSlug: item.tenantSlug,
          method: item.method,
          url: item.url,
          body: item.body,
          createdAt: Date.now(),
          attempts: 0,
          lastAttemptAt: null,
          lastError: null,
        }
        set((s) => ({ items: [record, ...s.items] }))
        return id
      },

      remove: (id) => {
        set((s) => ({ items: s.items.filter((i) => i.id !== id) }))
      },

      clearTenant: (tenantSlug) => {
        set((s) => ({ items: s.items.filter((i) => i.tenantSlug !== tenantSlug) }))
      },

      pendingCount: () => {
        const currentTenant = resolveTenantSlug()
        return get().items.filter((i) => i.tenantSlug === currentTenant).length
      },

      flush: async () => {
        const currentTenant = resolveTenantSlug()
        if (!currentTenant) return

        const items = get().items
          .filter((i) => i.tenantSlug === currentTenant)
          .slice()
          .reverse()

        for (const item of items) {
          try {
            set((s) => ({
              items: s.items.map((i) =>
                i.id === item.id
                  ? { ...i, attempts: i.attempts + 1, lastAttemptAt: Date.now(), lastError: null }
                  : i
              ),
            }))

            await apiClient.request({
              method: item.method,
              url: item.url,
              data: item.body,
            })

            get().remove(item.id)
          } catch (err) {
            // Stop on network error to avoid draining battery / log spam.
            if (axios.isAxiosError(err) && !err.response) {
              return
            }

            const message = axios.isAxiosError(err)
              ? `${err.response?.status ?? 'error'} ${JSON.stringify(err.response?.data ?? {})}`.slice(0, 1000)
              : err instanceof Error
                ? err.message
                : 'Unknown error'

            set((s) => ({
              items: s.items.map((i) => (i.id === item.id ? { ...i, lastError: message } : i)),
            }))
          }
        }
      },
    }),
    {
      name: 'outbox-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
)

